import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import React, { Dispatch, JSX, SetStateAction, useMemo, useState } from 'react'
import { useMediaQuery } from 'react-responsive'

export interface DropdownType {
  id: string | number
  title: string
}

export interface DropdownProps {
  testId: string
  data: DropdownType[]
  setSelected: Dispatch<SetStateAction<any>>
  selected?: DropdownType
  placeholderText?: string
  allOption?: boolean
  disabled: boolean
}
export const Dropdown = ({ testId, data, setSelected, selected, placeholderText = 'Select', allOption = false, disabled }: DropdownProps): JSX.Element => {
  const [query, setQuery] = useState('')
  const all: DropdownType = { id: 'all', title: 'All' }
  const isMobile = useMediaQuery({ maxWidth: 639 })

  const sortedData = useMemo(() => {
    const sorted = [...data].sort((a, b) => a.title.localeCompare(b.title))
    return allOption ? [all, ...sorted] : sorted
  }, [data, allOption, all])

  const filteredData = useMemo(() => {
    return sortedData.filter((item) =>
      item.title.toLowerCase().includes(query.toLowerCase())
    )
  }, [query, sortedData])

  const noResults = (
    <div className='px-4 py-2 dark:text-white'>No results found</div>
  )

  const disabledClass = disabled ? 'pointer-events-none opacity-50' : ''

  const optionsList = filteredData.map((item) => (
    <ComboboxOption
      data-testid={`${testId}-option-${item.id}`}
      key={item.id}
      value={item}
      className={({ focus }) =>
        `block w-full text-left px-4 py-2 cursor-default select-none ${
          focus ? 'bg-lm-bg-secondary dark:bg-dm-bg-secondary' : ''
        }`}
    >

      {item.title}
    </ComboboxOption>
  ))

  const dropdownOptions = filteredData.length === 0 && query !== ''
    ? noResults
    : optionsList

  return (
    <Combobox
      disabled={disabled}
      value={selected ?? all}
      onChange={(value) => {
        if (value != null) setSelected(value)
      }}
    >
      <div className={`relative ${disabledClass}`}>
        <div className='relative w-full'>
          <ComboboxInput
            onFocus={() => setQuery('')}
            autoComplete='off'
            autoCorrect='off'
            spellCheck='false'
            name='input-drop'
            readOnly={disabled || isMobile}
            disabled={disabled}
            data-testid={`${testId}-input`}
            className='w-full rounded-md px-3 py-2 pr-10 text-sm font-semibold ring-1 shadow-xs dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 ring-black/5 ring-inset focus:outline-none focus:ring-2 focus:ring-primary placeholder-black dark:placeholder-white text-ellipsis'
            displayValue={() => ''}
            onChange={(event) => { if (!isMobile) setQuery(event.target.value) }}
            placeholder={selected?.title ?? placeholderText}
          />
          <ComboboxButton
            data-testid={`${testId}-trigger`}
            className='absolute inset-y-0 right-0 flex items-center pr-2'
          >
            <ChevronDownIcon
              className='h-5 w-5 text-lm-text-secondary dark:text-dm-text-secondary dark:text-gray-300'
              aria-hidden='true'
            />
          </ComboboxButton>
        </div>
        <ComboboxOptions className='absolute z-10 mt-2 w-full origin-top-right rounded-md bg-lm-bg-main dark:bg-dm-bg-main ring-1 shadow-lg dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 ring-black/5 max-h-60 overflow-auto focus:outline-none text-sm'>
          {dropdownOptions}
        </ComboboxOptions>
      </div>
    </Combobox>
  )
}
